import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import {
  Box, Checkbox, CircularProgress, ListItemText,
  MenuItem, Select, TextField, Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import i18next from "i18next";
import { BRAND } from "../lightTheme";
import { AttributeType } from "services/common/Constants";
import { hasAttributeListItems } from "services/common/AttributeUtils";
import { getAccountAttributeById } from "services/edge/ContexteService";
import { Context } from "states/Store";

const useStyles = makeStyles()(() => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.85rem 1.25rem",
    "@media (max-width: 560px)": {
      gridTemplateColumns: "1fr",
    },
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: "0.3rem",
  },
  label: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  required: {
    color: BRAND.primary,
    fontWeight: 700,
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontFamily: "Inter, sans-serif",
      fontSize: "0.875rem",
      backgroundColor: BRAND.background,
      "& fieldset": { borderColor: BRAND.border },
      "&:hover fieldset": { borderColor: BRAND.primary },
      "&.Mui-focused fieldset": { borderColor: BRAND.primary },
    },
  },
  inputError: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d32f2f" },
    },
  },
  select: {
    borderRadius: "10px !important",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    backgroundColor: BRAND.background,
    "& fieldset": { borderColor: BRAND.border },
    "&:hover fieldset": { borderColor: BRAND.primary },
    "&.Mui-focused fieldset": { borderColor: BRAND.primary },
  },
  selectError: {
    "& fieldset": { borderColor: "#d32f2f !important" },
  },
  placeholder: {
    color: BRAND.textMuted,
    fontStyle: "italic",
    fontSize: "0.875rem",
    fontFamily: "Inter, sans-serif",
  },
  loader: {
    display: "flex",
    alignItems: "center",
    height: "40px",
    paddingLeft: "0.5rem",
  },
  error: {
    fontSize: "0.72rem",
    color: "#d32f2f",
    fontFamily: "Inter, sans-serif",
  },
}));

function isValueEmpty(type, value) {
  if (type === AttributeType.LIST_MULTIPLE) {
    return !Array.isArray(value) || value.length === 0;
  }
  return !value || value === "";
}

function AttrRow({ attr, value, onChange, showError }) {
  const { classes: s, cx } = useStyles();
  const [state] = useContext(Context);
  const [listItems, setListItems] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (!hasAttributeListItems(attr.type)) return;
    setLoadingList(true);
    (async () => {
      try {
        const res = await getAccountAttributeById(state.account.id, attr.attributeId ?? attr.id);
        const items = res.content?.values
          ?.map((x) => x.value)
          ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) ?? [];
        setListItems(items);
      } catch (_) {
        setListItems([]);
      } finally {
        setLoadingList(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attr.attributeId, attr.id]);

  const labelText = attr.label ?? attr.id;
  const hasError = showError && isValueEmpty(attr.type, value);

  const renderInput = () => {
    if (loadingList) {
      return (
        <Box className={s.loader}>
          <CircularProgress size={16} style={{ color: BRAND.primary }} />
        </Box>
      );
    }

    switch (attr.type) {
      case AttributeType.LIST_UNIQUE:
        return (
          <Select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? [] : [e.target.value])}
            size="small"
            displayEmpty
            className={cx(s.select, hasError && s.selectError)}
            renderValue={(v) => v ? <span>{v}</span> : <span className={s.placeholder}>{i18next.t("notSpecified")}</span>}
          >
            <MenuItem value="" style={{ fontFamily: "Inter", fontSize: "0.875rem", color: BRAND.textMuted, fontStyle: "italic" }}>
              {i18next.t("notSpecified")}
            </MenuItem>
            {(listItems ?? []).map((item) => (
              <MenuItem key={item} value={item} style={{ fontFamily: "Inter", fontSize: "0.875rem" }}>
                {item}
              </MenuItem>
            ))}
          </Select>
        );

      case AttributeType.LIST_MULTIPLE: {
        const selected = Array.isArray(value) ? value : [];
        return (
          <Select
            multiple
            value={selected}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            displayEmpty
            className={cx(s.select, hasError && s.selectError)}
            renderValue={(v) =>
              v.length === 0
                ? <span className={s.placeholder}>{i18next.t("notSpecified")}</span>
                : v.join(", ")
            }
          >
            {(listItems ?? []).map((item) => (
              <MenuItem key={item} value={item} style={{ fontFamily: "Inter", fontSize: "0.875rem" }}>
                <Checkbox checked={selected.includes(item)} size="small" style={{ color: BRAND.primary }} />
                <ListItemText primary={item} primaryTypographyProps={{ style: { fontFamily: "Inter", fontSize: "0.875rem" } }} />
              </MenuItem>
            ))}
          </Select>
        );
      }

      case AttributeType.BOOLEAN:
        return (
          <Select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? [] : [e.target.value])}
            size="small"
            displayEmpty
            className={cx(s.select, hasError && s.selectError)}
            renderValue={(v) => v ? <span>{v === "true" ? i18next.t("yes") : i18next.t("no")}</span> : <span className={s.placeholder}>{i18next.t("notSpecified")}</span>}
          >
            <MenuItem value="" style={{ fontFamily: "Inter", fontSize: "0.875rem", color: BRAND.textMuted, fontStyle: "italic" }}>{i18next.t("notSpecified")}</MenuItem>
            <MenuItem value="true" style={{ fontFamily: "Inter", fontSize: "0.875rem" }}>{i18next.t("yes")}</MenuItem>
            <MenuItem value="false" style={{ fontFamily: "Inter", fontSize: "0.875rem" }}>{i18next.t("no")}</MenuItem>
          </Select>
        );

      case AttributeType.DATE:
        return (
          <TextField
            type="date"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
            size="small"
            className={cx(s.input, hasError && s.inputError)}
            InputLabelProps={{ shrink: true }}
          />
        );

      default:
        return (
          <TextField
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
            placeholder={i18next.t("notSpecified")}
            size="small"
            className={cx(s.input, hasError && s.inputError)}
            inputProps={{ style: { fontFamily: "Inter", fontSize: "0.875rem" } }}
          />
        );
    }
  };

  return (
    <Box className={s.row}>
      <Typography className={s.label}>
        {labelText}
        {attr.isRequired && <span className={s.required}>*</span>}
      </Typography>
      {renderInput()}
      {hasError && (
        <Typography className={s.error}>{i18next.t("light.common.requiredField")}</Typography>
      )}
    </Box>
  );
}

/**
 * Light-mode attributes form.
 * Exposes via ref:
 *   - validate(): boolean — marks required empty fields as errors, returns true if valid
 *   - getValues(): array — returns current attribute array with updated values
 */
const LightAttributesForm = forwardRef(function LightAttributesForm({ attributes }, ref) {
  const { classes: s } = useStyles();
  const [localAttrs, setLocalAttrs] = useState(() => attributes.map((a) => ({ ...a })));
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    setLocalAttrs(attributes.map((a) => ({ ...a })));
    setShowErrors(false);
  }, [attributes]);

  useImperativeHandle(ref, () => ({
    validate() {
      const hasInvalid = localAttrs.some(
        (a) => a.isRequired && isValueEmpty(a.type,
          a.type === AttributeType.LIST_MULTIPLE ? (a.values ?? []) : (a.values?.[0] ?? "")
        )
      );
      if (hasInvalid) setShowErrors(true);
      return !hasInvalid;
    },
    getValues() {
      return localAttrs;
    },
  }));

  const handleChange = (index, newValues) => {
    setLocalAttrs((prev) => prev.map((a, i) => i === index ? { ...a, values: newValues } : a));
    // Clear errors as soon as user fixes something
    setShowErrors(false);
  };

  if (localAttrs.length === 0) return null;

  return (
    <Box className={s.grid}>
      {localAttrs.map((attr, i) => (
        <AttrRow
          key={attr.attributeId ?? attr.id ?? i}
          attr={attr}
          value={
            attr.type === AttributeType.LIST_MULTIPLE
              ? (attr.values ?? [])
              : (attr.values?.[0] ?? "")
          }
          onChange={(newValues) => handleChange(i, newValues)}
          showError={showErrors && attr.isRequired}
        />
      ))}
    </Box>
  );
});

export default LightAttributesForm;
